import eventMetaMap from '../constants/slEventBusEventNames';
import loggerService from '../utils/loggerService';

// output: [ { name: 'general.currencyChanged' } ...]
const eventList = Object.keys(eventMetaMap).flatMap((groupKey) =>
  Object.values(eventMetaMap[groupKey]),
);

function validateEventName(eventName, caller) {
  if (typeof eventName !== 'string' || !eventName) {
    // eslint-disable-next-line no-restricted-syntax
    console.warn(
      `[slEventBus] ${caller}() requires a non-empty string as eventName.`,
    );
    return;
  }
  const eventItem = eventList.find((entry) => entry.name === eventName);
  if (!eventItem) {
    // eslint-disable-next-line no-restricted-syntax
    console.warn(`[slEventBus] "${eventName}" is not a registered event.`);
  }
}

function createEventBus() {
  const eventMap = new Map();
  /**
   * Register a listener for an event.
   * @param {string} eventName
   * @param {Function} callback
   * @param {{ debug?: boolean }} options
   * @returns {Function} Unsubscribe function
   */
  function on(eventName, callback, options) {
    if (options?.debug) validateEventName(eventName, 'on');
    if (!callback) return;

    if (!eventMap.has(eventName)) {
      eventMap.set(eventName, new Set());
    }

    eventMap.get(eventName).add(callback);

    // support anonymous callback to unsubscription
    return () => off(eventName, callback);
  }

  /**
   * Register a one-time listener that auto-removes after first invocation.
   * @param {string} eventName
   * @param {Function} callback
   * @param {{ debug?: boolean }} options
   * @returns {Function} Unsubscribe function
   */
  function once(eventName, callback, options) {
    if (options?.debug) validateEventName(eventName, 'once');
    if (!callback) return;

    const wrapper = (payload) => {
      off(eventName, wrapper);
      callback(payload);
    };

    wrapper.originalCallback = callback;

    on(eventName, wrapper);

    return () => off(eventName, wrapper);
  }

  /**
   * Remove a specific listener for an event.
   * @param {string} eventName
   * @param {Function} callback
   * @param {{ debug?: boolean }} options
   */
  function off(eventName, callback, options) {
    if (options?.debug) validateEventName(eventName, 'off');
    if (!callback) return;

    if (!eventMap.has(eventName)) return;

    const listeners = eventMap.get(eventName);

    // Support comparison of regular callbacks and wrappers created by once
    for (let listener of listeners) {
      if (listener === callback || listener.originalCallback === callback) {
        listeners.delete(listener);
        break;
      }
    }

    if (listeners.size === 0) {
      eventMap.delete(eventName);
    }
  }

  /**
   * Emit an event, calling all registered listeners with the provided payload.
   * @param {string} eventName
   * @param {...*} payload
   */
  function emit(eventName, payload) {
    validateEventName(eventName, 'emit');
    const listeners = eventMap.get(eventName);
    if (!listeners || listeners.size === 0) return;

    const listenersCopy = Array.from(listeners);

    listenersCopy.forEach((cb) => {
      try {
        cb(payload);
      } catch (error) {
        loggerService.warn(error);
      }
    });
  }

  return { on, once, off, emit };
}

// Share a single instance across webpack bundles via window
if (!window?.shopline?.slEventBus) {
  window.shopline = {
    ...(window?.shopline || {}),
    slEventBus: createEventBus(),
  };
}

export default window.shopline.slEventBus;
export { eventMetaMap };
